import React from 'react';
import HelpHint from './HelpHint';

export interface SettingRowProps {
  /** What the setting is, in a few words. */
  label: React.ReactNode;
  /** One short line under the label: what it does or its current state. */
  description?: React.ReactNode;
  /** The long explanation, behind a «?» next to the label (never a paragraph
   *  under the field: Standards §14.8). */
  help?: React.ReactNode;
  /** The control: an `Input`, a `DropdownSelect`, a `Switch`, a `Button`… */
  children: React.ReactNode;
  /** Extra line(s) under the control (a stored secret's mask, «Borrar valor»). */
  footer?: React.ReactNode;
  /** `id` for the control's label (`aria-labelledby`), when the control has
   *  no visible label of its own. */
  labelId?: string;
  className?: string;
}

/**
 * SettingRow — one setting as a row (Standards §20.12): what it is on the
 * left, the control on the right; on a phone the control drops under the
 * label. Rows stack inside one card, separated by a hairline — never a
 * bordered box per setting.
 *
 * The row draws no card of its own: the screen puts its rows in one
 * (`sectionBox`/`cardClass`), so a list of settings reads as a list.
 *
 * @example
 * <SettingRow label="Email del superadmin" description="Único que puede borrar usuarios" help={<p>…</p>}>
 *   <Input aria-label="Email del superadmin" value={v} onChange={…} />
 * </SettingRow>
 */
export default function SettingRow({ label, description, help, children, footer, labelId, className = '' }: SettingRowProps) {
  return (
    <div
      className={`grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_minmax(0,24rem)] gap-x-6 gap-y-2 items-start py-3 border-b border-border-subtle dark:border-border-subtle-dark last:border-b-0 ${className}`}
    >
      <div className="min-w-0 pt-1">
        <div className="flex items-center gap-1.5">
          <span id={labelId} className="text-sm font-medium text-text-main dark:text-white">{label}</span>
          {help ? <HelpHint label={typeof label === 'string' ? `Ayuda: ${label}` : 'Ayuda'}>{help}</HelpHint> : null}
        </div>
        {description ? <p className="mt-0.5 text-xs text-text-sub dark:text-slate-400">{description}</p> : null}
      </div>
      <div className="min-w-0">
        {children}
        {footer ? <div className="mt-1 text-xs text-text-sub dark:text-slate-400">{footer}</div> : null}
      </div>
    </div>
  );
}
